import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { ERROR_MESSAGE, LOCAL_STORAGE_KEY, TOKEN_API_ENDPOINT, ROUTER_CONFIG, HttpStatus, ErrorMessageResponse } from '@core/configs';
import { ConstantOfTriggerSession, KeyTriggerSession, LocalStorageHelper, LocalStorageKey } from '@core/helper/local-storage.helper';
import { ApiResponse } from '@core/models/interfaces';
import { ApiService, AutoLogoutService, HttpCancelService, LocalStorageService, RequestService } from '@core/services/common';
import { BehaviorSubject, lastValueFrom, Observable, throwError } from 'rxjs';
import { AuthConsoleLoginRO, ErrorResponseRO } from '@core/models/ro';
import { getFirstURL } from '@core/helper';
import { AuthConsoleService } from '../auth-console.service';
import { AuthConsoleLoginByTokenDTO } from '@core/models/dto/auth-console.dto';
import { LoginFlowService } from '../common/login-flow.service';
import { environment } from '@environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  jwtHelperService: JwtHelperService = new JwtHelperService();
  private hasQueryTokenSubject = new BehaviorSubject<boolean>(false);
  hasQueryToken = this.hasQueryTokenSubject.asObservable();

  constructor(
    private readonly http: ApiService,
    private router: Router,
    private autoLogoutService: AutoLogoutService,
    private cancelService: HttpCancelService,
    private requestService: RequestService,
    private localStorageService: LocalStorageService,
    private authConsoleService: AuthConsoleService,
    private loginFlowService: LoginFlowService
  ) { }

  setHasQueryToken(val: boolean) {
    this.hasQueryTokenSubject.next(val);
  }

  get isAccessTokenExpired(): boolean {
    const accessToken = this.localStorageService.getAccessToken();
    if (accessToken) {
      return this.jwtHelperService.isTokenExpired(accessToken);
    }
    return true;
  }

  get isRefreshTokenExpired(): boolean {
    const refreshToken = this.localStorageService.getRefreshToken();
    if (refreshToken) {
      return this.jwtHelperService.isTokenExpired(refreshToken);
    }
    return true;
  }

  loggedIn() {
    if (this.isRefreshTokenExpired) {
      if (this.localStorageService.getItem(LOCAL_STORAGE_KEY.userSession)) {
        this.logout();
      }
      return false;
    } else {
      return true;
    }
  }

  getModuleByToken(token: string): string {
    return '';
  }

  async handleTokenLogin(queryAccessToken?: string, queryRefreshToken?: string): Promise<boolean> {
    if (!queryAccessToken || !queryRefreshToken) return false;

    const loginAndHandle = async () => {
      const reqBody: AuthConsoleLoginByTokenDTO = {
        accessToken: queryAccessToken,
        refreshToken: queryRefreshToken
      }
      
      let isSuccess = true;
      try {
        const res = await lastValueFrom(this.authConsoleService.loginByToken(reqBody));
        isSuccess = await this.loginFlowService.handleLoginSuccess({
          loginRo: res?.result?.response,
          menuRo: res?.result?.response?.menus
        });
      } catch(err: any) {
        isSuccess = false;

        const e = err?.error;
        const code = Number(e?.code || 0);
        const message = e?.message;

        if (code === HttpStatus.UNAUTHORIZED) {
          if (typeof message === 'object' && message?.code === ErrorMessageResponse.LOCK_USER) {
            window.location.href = environment.PASS_URL + 'blocked-user?module=auth';
          }
        }
      }
  
      return isSuccess;
    };

    if (!this.loggedIn()) {
      return await loginAndHandle();
    }

    const currentUser = LocalStorageHelper.getUserSession();
    const userId = currentUser?.userId;

    const isCurrentUser = this.isCurrentUser(queryAccessToken, userId);
    if (isCurrentUser) {
      this.router.navigate([getFirstURL()]);
      return true;
    }

    // await this.authenticationService.logout(false);
    return await loginAndHandle();

    // const decodeToken = this.jwtHelperService.decodeToken(params.accessToken);

    // if (!decodeToken || !decodeToken.userId) {
    //   throw new Error('Invalid token!');
    // }

    // const userSessionLocal = LocalStorageHelper.getUserSession();
    // const userSessionObject: AuthConsoleLoginRO = {
    //   accessToken: params.accessToken,
    //   refreshToken: params.refreshToken,
    // };

    // if (userSessionLocal && userSessionLocal.userId) {
    //   const isCurrentUser = this.isCurrentUser(decodeToken.userId);
    //   if (isCurrentUser) {
    //     sessionStorage.setItem(LocalStorageKey.SESSION_LOGIN, LocalStorageKey.SESSION_LOGIN);
    //     LocalStorageHelper.setUserSession(userSessionObject);
    //     LocalStorageHelper.trigger(ConstantOfTriggerSession.SET_TOKEN);
    //   } else {
    //     // Logout and login with the accessToken passed in
    //     this.logout(false).then(() => {
    //       sessionStorage.setItem(LocalStorageKey.SESSION_LOGIN, LocalStorageKey.SESSION_LOGIN);
    //       LocalStorageHelper.setUserSession(userSessionObject);
    //       LocalStorageHelper.trigger(ConstantOfTriggerSession.SET_TOKEN);
    //     });
    //   }
    // } else {
    //   sessionStorage.setItem(LocalStorageKey.SESSION_LOGIN, LocalStorageKey.SESSION_LOGIN);
    //   LocalStorageHelper.setUserSession(userSessionObject);
    //   LocalStorageHelper.trigger(ConstantOfTriggerSession.SET_TOKEN);
    // }

  }

  requestAccessToken(refreshToken: string): Observable<ApiResponse<any>> {
    if (refreshToken) {
      return this.http.post(TOKEN_API_ENDPOINT.refreshToken, {
        refreshToken,
      });
    } else {
      return throwError(ERROR_MESSAGE.refreshTokenNotFound);
    }
  }

  async logout(isNavigateToLogin: boolean = true, isLog = false) {
    if (!this.requestService.checkCompletedRequests()) {
      this.cancelService.cancelPendingRequest();
    }

    if (!this.isAccessTokenExpired || !this.isRefreshTokenExpired) {
      this.callLogout()
        .toPromise()
        .catch(() => {
          this.callLogout();
        });
    }
    this.autoLogoutService.stop();
    this.localStorageService.clear();
    sessionStorage.clear();
    this.localStorageService.trigger(KeyTriggerSession.REMOVE_TOKEN);
    if (isNavigateToLogin) {
      this.router.navigate([ROUTER_CONFIG.auth.login]);
    }
  }

  private callLogout() {
    return this.http.delete(TOKEN_API_ENDPOINT.logout);
  }

  isCurrentUser(token: string, currentUserId: any): boolean {
    const jwtHelper = new JwtHelperService();
    const decodeToken = jwtHelper.decodeToken(token);
    if (!decodeToken || !decodeToken.userId) {
        throw new Error('Invalid token!');
    }
    return Number(currentUserId) === Number(decodeToken.userId);
  }

  verifyRefreshToken(refreshToken: string) {
    return this.http.post(TOKEN_API_ENDPOINT.verifyRefreshToken, { refreshToken });
  }
}
